"use client";

import React, { createContext, useContext, useMemo } from "react";
import type { AuthCredential } from "firebase/auth";

export interface AuthUser {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
    isAnonymous: boolean;
}

export type DialogRequest =
    | { kind: "collision-email"; email: string; password: string }
    | { kind: "collision-google"; email: string | null; credential: AuthCredential | null }
    | { kind: "switch-from-guest" };

export type DialogChoice = "sign-in-existing" | "use-different-email" | "keep-guest";

export interface AuthDialogState {
    request: DialogRequest | null;
    choose: (choice: DialogChoice) => void;
}

export interface AuthContextType {
    user: AuthUser | null;
    loading: boolean;
    isAuthEnabled: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string, displayName?: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    dialog: AuthDialogState;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

export function useAuthDialog(): AuthDialogState {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuthDialog must be used inside AuthProvider");
    return ctx.dialog;
}

const authDisabledError = () => Promise.reject(new Error("Authentication not configured"));

/**
 * Lightweight provider for tenants with auth disabled.
 * No Firebase imports — zero bundle cost.
 */
export function AuthProviderDisabled({ children }: { children: React.ReactNode }) {
    // stub — replaced in Task 6
    const dialog: AuthDialogState = useMemo(() => ({ request: null, choose: () => {} }), []);

    const value: AuthContextType = {
        user: null,
        loading: false,
        isAuthEnabled: false,
        signIn: authDisabledError,
        signUp: authDisabledError,
        signInWithGoogle: authDisabledError,
        signOut: authDisabledError,
        resetPassword: authDisabledError,
        dialog,
    };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
